// @ts-check

/**
 * Quote — scroll-triggered word highlight.
 *
 * Each word has two states only: 40% opacity (dim) or 100% (lit). As the quote
 * scrolls up a read-head advances through the words in reading order and trips
 * them a couple at a time; once tripped, a word rides its CSS transition the
 * whole way 40% → 100% and latches there (it never rests at an in-between
 * value). Every word is lit by the time the quote rises to ~60% of the
 * viewport height (see FILL_FROM_TOP). Reduced-motion, or no quote on the page,
 * leaves the text fully opaque.
 */

(function() {
    'use strict';

    const DIM = 0.4;
    // Words tripped per read-head step — a couple at a time.
    const STEP = 2;
    // Every word is lit once the quote's centre reaches this fraction down from
    // the top of the viewport — 0.4 = 40% from the top (60% up from the bottom).
    // Higher up than the middle stretches the fill over more scroll (more delay).
    const FILL_FROM_TOP = 0.4;

    const texts = /** @type {HTMLElement[]} */ (Array.from(
        document.querySelectorAll('.quote__text'),
    ));
    if (!texts.length) {
        return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
    }

    /**
     * Wrap every word in an element's text nodes in a `.quote__word` span,
     * preserving inline markup and whitespace. Each span starts dim (set before
     * first paint, so it doesn't animate in). Returns the created spans.
     *
     * @param {HTMLElement} el - The quote text block.
     * @returns {HTMLElement[]} The per-word spans, in document order.
     */
    function splitWords(el) {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        /** @type {Text[]} */
        const nodes = [];
        while (walker.nextNode()) {
            nodes.push(/** @type {Text} */ (walker.currentNode));
        }

        /** @type {HTMLElement[]} */
        const words = [];
        for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            const value = node.nodeValue || '';
            if (!value.trim()) {
                continue;
            }
            const frag = document.createDocumentFragment();
            const parts = value.split(/(\s+)/);
            for (let p = 0; p < parts.length; p++) {
                const part = parts[p];
                if (part === '') {
                    continue;
                }
                if (/^\s+$/.test(part)) {
                    frag.appendChild(document.createTextNode(part));
                } else {
                    const span = document.createElement('span');
                    span.className = 'quote__word';
                    span.style.opacity = String(DIM);
                    span.textContent = part;
                    frag.appendChild(span);
                    words.push(span);
                }
            }
            if (node.parentNode) {
                node.parentNode.replaceChild(frag, node);
            }
        }
        return words;
    }

    /** @type {{el: HTMLElement, words: HTMLElement[], lit: number, active: boolean}[]} */
    const groups = [];
    for (let t = 0; t < texts.length; t++) {
        const words = splitWords(texts[t]);
        if (words.length) {
            groups.push({el: texts[t], words: words, lit: 0, active: true});
        }
    }
    if (!groups.length) {
        return;
    }

    let ticking = false;

    function update() {
        ticking = false;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const fillLine = vh * FILL_FROM_TOP;
        const denom = (vh - fillLine) || 1;
        for (let g = 0; g < groups.length; g++) {
            const grp = groups[g];
            if (!grp.active) {
                continue;
            }
            // Quote progress: 0 when the block's centre sits at the fold,
            // 1 when it reaches the fill line (FILL_FROM_TOP up the viewport).
            const rect = grp.el.getBoundingClientRect();
            const blockCentre = rect.top + rect.height / 2;
            let progress = (vh - blockCentre) / denom;
            if (progress < 0) {
                progress = 0;
            } else if (progress > 1) {
                progress = 1;
            }
            // How many words should be lit — quantised to STEP-sized groups and
            // latched, so words only ever trip on (never revert to dim).
            const words = grp.words;
            let want = Math.round((progress * words.length) / STEP) * STEP;
            if (want > words.length) {
                want = words.length;
            }
            for (let i = grp.lit; i < want; i++) {
                words[i].style.opacity = '1';
            }
            if (want > grp.lit) {
                grp.lit = want;
            }
        }
    }

    function onScroll() {
        if (!ticking) {
            ticking = true;
            requestAnimationFrame(update);
        }
    }

    if (typeof IntersectionObserver !== 'undefined') {
        const io = new IntersectionObserver((entries) => {
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                for (let g = 0; g < groups.length; g++) {
                    if (groups[g].el === entry.target) {
                        groups[g].active = entry.isIntersecting;
                    }
                }
            }
            onScroll();
        }, {rootMargin: '100% 0px 100% 0px'});
        for (let g = 0; g < groups.length; g++) {
            io.observe(groups[g].el);
        }
    }

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    update();
})();
